var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment } from 'react';
import { useForm } from 'react-hook-form';
import { boolean, object } from 'yup';
import { Button, Checkbox } from '@paysera/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useAcceptTermsAndConditionsMutation } from '@app/api/termsAndConditionsApi';
import { getUserLocale } from '@app/utils/getUserLocale';
const schema = object()
    .shape({
    borrower_tc_terms: boolean().oneOf([true]),
    borrower_tc_privacy: boolean().oneOf([true]),
    borrower_tc_consent: boolean().oneOf([true]),
    borrower_tc_marketing: boolean(),
})
    .required();
const TermsAndConditions = ({ agreements }) => {
    const { register, handleSubmit, formState: { errors }, } = useForm({
        resolver: yupResolver(schema),
    });
    const { t } = useTranslation();
    const [acceptTerms, { isLoading }] = useAcceptTermsAndConditionsMutation();
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        const dataToSubmit = Object.keys(data).filter((key) => data[key]);
        yield acceptTerms(dataToSubmit);
    });
    const locale = getUserLocale();
    return (<form className="flex flex-wrap gap-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex-1 min-w-[320px]">
                <div className="flex flex-col gap-4">
                    <h3 className="title-m">{t('financing.borrowing_page.welcome_to')}</h3>
                    <p className="body-l">{t('financing.borrowing_page.join_service')}</p>
                </div>

                <div className="flex flex-col gap-4 mt-6">
                    <h4 className="title-m">{t('financing.borrowing_page.how_it_works')}</h4>
                    <ol className="list-decimal pl-5 space-y-1 marker:font-semibold">
                        <li className="body-l">
                            <span className="title-m">{t('financing.borrowing_page.apply_for_loan')}</span>
                            <span className="ms-1">{t('financing.borrowing_page.submit_application')}</span>
                        </li>
                        <li className="body-l">
                            <span className="title-m">{t('financing.borrowing_page.credit_assessment')}</span>
                            <span className="ms-1">{t('financing.borrowing_page.assess_credit_score')}</span>
                        </li>
                        <li className="body-l">
                            <span className="title-m">{t('financing.borrowing_page.select_terms')}</span>
                            <span className="ms-1">{t('financing.borrowing_page.choose_loan_amount')}</span>
                        </li>
                        <li className="body-l">
                            <span className="title-m">{t('financing.borrowing_page.funding')}</span>
                            <span className="ms-1">{t('financing.borrowing_page.receive_funds')}</span>
                        </li>
                        <li className="body-l">
                            <span className="title-m">{t('financing.borrowing_page.repayment')}</span>
                            <span className="ms-1">{t('financing.borrowing_page.make_payments')}</span>
                        </li>
                    </ol>

                    <div>
                        <p className="title-m">{t('financing.borrowing_page.legal_info')}</p>
                        <a className="body-l text-action-primary-initial underline" href={`https://www.paysera.com/v2/${locale}/legal`} target="_blank" rel="noreferrer">
                            {t('financing.borrowing_page.legal_info_link')}
                        </a>
                    </div>
                </div>
            </div>

            <div className="w-full md:w-[400px]">
                <div className="flex flex-col gap-4 rounded-lg p-6 bg-soft-gradient">
                    <div className="flex flex-col gap-4">
                        <h3 className="title-m">{t('financing.borrowing_page.become_borrower')}</h3>
                        <p className="body-l">{t('financing.borrowing_page.agree_terms')}</p>
                    </div>

                    <div className="flex flex-col gap-3">
                        <hr className="border-neutral-primary-initial"/>
                        <p className="title-s">{t('financing.borrowing_page.read_and_agreed')}</p>
                        <div className="flex flex-col gap-2">
                            {agreements === null || agreements === void 0 ? void 0 : agreements.map((agreement, index) => (<Fragment key={agreement.name}>
                                    <Checkbox className="min-w-4 items-start" variant={(errors === null || errors === void 0 ? void 0 : errors[agreement.name]) ? 'critical' : 'default'} {...register(agreement.name)} label={<>
                                                {`${t(`financing.borrowing_page.${agreement.name}_part1`)}`}
                                                {(agreement === null || agreement === void 0 ? void 0 : agreement.url) && (<a href={agreement.url} target="_blank" className="text-action-primary-initial" rel="noreferrer">
                                                        {` ${t(`financing.borrowing_page.${agreement.name}_link`)}`}
                                                    </a>)}
                                                .
                                            </>}/>
                                    {index === 2 && Object.keys(errors).length > 0 && (<p className="text-critical-primary-initial body-s">
                                            {t('financing.borrowing_page.terms_error')}
                                        </p>)}
                                </Fragment>))}
                        </div>
                    </div>

                    <div>
                        <Button type="submit" variant="primary" size="lg" disabled={isLoading} loading={isLoading} label={t('financing.borrowing_page.continue')} isFullWidth/>
                    </div>
                </div>
            </div>
        </form>);
};
export default TermsAndConditions;
